/**
 * Configuration settings for the VinSpectorAI application
 * Supports different environments through environment variables
 * Last Updated: 2025-05-21
 */

// Determine the current environment
const isProduction = process.env.NODE_ENV === 'production';
const isStaging = process.env.REACT_APP_ENV === 'staging';

// Set base URLs according to environment
let backendUrl;
if (isProduction) {
  backendUrl = 'https://api.vinspectorai.com'; // Use your API subdomain
} else if (isStaging) {
  backendUrl = 'https://staging.vinspectorai.fly.dev';
} else {
  // Development environment - use local server or specified dev URL
  backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
}

const config = {
  // Backend API base URLs
  API: {
    // Main backend API
    BACKEND_URL: backendUrl,

    // All API endpoints - centralized for easy reference
    ENDPOINTS: {
      // Vehicle data APIs
      CLEARVIN: '/api/clearvin', // Premium/business vehicle data
      VIN_LOOKUP: '/api/vin/lookup', // Alternative endpoint for vehicle data
      NHTSA: 'https://vpic.nhtsa.dot.gov/api/vehicles', // Free tier vehicle data (legacy - now uses ClearVin Preview)
      CLEARVIN_PREVIEW: '/api/nhtsa/vin', // Free tier using ClearVin Preview API

      // Need to add the VEHICLE section to match what we referenced in apiService
      VEHICLE: {
        // Updated based on logs - backend doesn't have /api/vehicle-reports endpoint
        // Consider adding this endpoint on backend or remove from frontend
        REPORT: '/api/vin/lookup', // Vehicle report lookup
      },

      // AI APIs - these appear to be working correctly based on logs
      AI: {
        ANALYZE: '/api/ai/analyze', // AI analysis of vehicle data
        ANALYZE_VEHICLE: '/api/ai/analyze-vehicle', // Alternative endpoint for analysis
        CHAT: '/api/ai/chat', // AI chat functionality (premium, vehicle-specific)
        SEARCH_TIP: '/api/ai/search-tip', // AI tips for search
        FREE_SUGGESTION: '/api/ai/free-suggestion', // AI suggestions for free users (legacy)
        VINI_CHAT: '/api/ai/vini-chat', // Vini site-wide assistant (free & premium general)
        COMPARE_VEHICLES: '/api/ai/compare-vehicles', // AI vehicle comparison
      },

      // Blog APIs - new section for blog post integration
      BLOG: {
        POSTS: '/api/blog/posts', // Get all blog posts or search
        POST_BY_ID: '/api/blog/posts/:id', // Get single blog post by ID or slug
        RELATED: '/api/blog/posts/:id/related', // Get related blog posts
        CATEGORIES: '/api/blog/categories', // Get blog categories
        TAGS: '/api/blog/tags', // Get blog tags
      },

      // Payment APIs - updated to match backend paths based on logs
      PAYMENT: {
        CHECKOUT: '/api/create-checkout-session', // Stripe checkout
        WEBHOOK: '/api/stripe-webhook', // Stripe webhook
        PORTAL: '/api/create-portal-session', // Customer portal session
        DETAILS: '/api/payments/details', // Corrected based on logs
      },

      // User management - updated to match backend paths based on logs
      USER: {
        PROFILE: '/api/users/profile', // User profile management
        SEARCHES: '/api/users/searches', // User search history
        // These three endpoints need to be updated based on logs
        CHECK_CREDITS: '/api/users/check-credits', // Corrected
        SEARCH_LIMIT: '/api/users/search-limit', // Added based on logs
        SEARCHES_USED: '/api/users/searches-used', // Added based on logs
        INCREMENT_SEARCH: '/api/users/increment-search', // Original path
      },

      // System APIs
      SYSTEM: {
        HEALTH: '/health', // System health check
        VERSION: '/version', // Version information
      },
    },
  },

  // External API direct URLs (for reference only - should use backend proxies in production)
  EXTERNAL_APIS: {
    NHTSA_URL: 'https://vpic.nhtsa.dot.gov/api/vehicles', // Legacy NHTSA (replaced by ClearVin Preview)
    CLEARVIN_URL: 'https://www.clearvin.com/rest/vendor',
    CLEARVIN_PREVIEW_URL: 'https://www.clearvin.com/rest/vendor/preview', // Free tier preview endpoint
    DEEPSEEK_URL: 'https://api.deepseek.com/v1',
    STRIPE_URL: 'https://api.stripe.com',
  },

  // Feature flags
  FEATURES: {
    USE_DEEP_SEEK: process.env.REACT_APP_USE_DEEP_SEEK !== 'false', // enabled by default
    ENABLE_DEBUG_LOGS: process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true',
    ENABLE_PREMIUM_FEATURES: true,
    ENABLE_AI_CHAT: true,
    ENABLE_VEHICLE_COMPARISON: true,
    ENABLE_BLOG_INTEGRATION: true, // New feature flag for blog integration
  },

  // API keys that are safe to expose in frontend (public API keys)
  // IMPORTANT: Never put sensitive API keys here - all sensitive keys should be on backend only
  PUBLIC_KEYS: {
    STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
  },

  // Payment settings
  PAYMENT: {
    STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
    DEFAULT_CURRENCY: 'usd',
    TAX_RATES: {
      US: 0.0, // No tax for digital services in the US
      CA: 0.13, // Example for Canada
      UK: 0.2, // Example for UK (VAT)
    },
  },

  // User membership tiers (for reference)
  MEMBERSHIP_TIERS: {
    FREE: 'free',
    PREMIUM: 'premium',
    BUSINESS: 'business',
  },

  // App information
  APP: {
    NAME: 'VinSpectorAI',
    VERSION: process.env.REACT_APP_VERSION || '1.0.2',
    BUILD_DATE: process.env.REACT_APP_BUILD_DATE || new Date().toISOString(),
    CONTACT_EMAIL: 'contact@vinspectorai.com',
  },

  // API timeouts in milliseconds - added based on API timeout issues
  TIMEOUTS: {
    DEFAULT: 15000, // 15 seconds for most API calls
    AI_OPERATIONS: 120000, // 120 seconds for AI operations (DeepSeek takes ~50-55s)
    BLOG_OPERATIONS: 10000, // 10 seconds for blog operations (typically faster)
  },

  // Blog configuration settings
  BLOG: {
    POSTS_PER_PAGE: 12,
    FEATURED_POST_COUNT: 3,
    RELATED_POST_COUNT: 3,
    EXCERPT_LENGTH: 160,
    CATEGORIES: [
      'Vehicle Maintenance',
      'Car Buying Tips',
      'Vehicle History',
      'Auto Safety',
      'Industry News',
    ],
  },
};

// Log configuration in development
if (!isProduction) {
  console.log('App Configuration:', {
    environment: isStaging ? 'staging' : 'development',
    backendUrl: config.API.BACKEND_URL,
    version: config.APP.VERSION,
    features: config.FEATURES,
  });
}

export default config;
